/**
 * Entropy bands from correction factor.
 * Authority: co-podr-dec-entropy-bands-1-7-rescaled-hard-top-20260928.
 * Lower bound inclusive, upper exclusive. CF >= 1 clamps to band 1.
 * Bands 8–10 are state labels, not numbers from this function.
 */

export function entropyFromCorrectionFactor(cf: number | null | undefined): number | null {
  if (cf == null || Number.isNaN(cf)) {
    return null;
  }
  const clamped = Math.min(1, cf);
  if (clamped >= 0.97) return 1;
  if (clamped >= 0.9) return 2;
  if (clamped >= 0.8) return 3;
  if (clamped >= 0.7) return 4;
  if (clamped >= 0.6) return 5;
  if (clamped >= 0.45) return 6;
  return 7;
}

/** Bands 8–10 are lifecycle labels. Never render them as a number. */
export function entropyStateLabel(entropy: number | null | undefined): string | null {
  if (entropy == null || Number.isNaN(entropy)) return null;
  if (entropy >= 10) return 'CREATED';
  if (entropy >= 9) return 'DRAFTED';
  if (entropy >= 8) return 'ACTIVE unmeasurable';
  return null;
}

export function derivedValueToCostRatio(
  valueGenerated: number | null | undefined,
  costToGenerate: number | null | undefined
): number | null {
  if (valueGenerated == null || costToGenerate == null) return null;
  if (!Number.isFinite(valueGenerated) || !Number.isFinite(costToGenerate) || costToGenerate === 0) {
    return null;
  }
  return valueGenerated / costToGenerate;
}

/** ideal value = cost × navigator ideal value-generation ratio. */
export function derivedIdealValue(
  costToGenerate: number | null | undefined,
  idealValueGenerationRatio: number | null | undefined
): number | null {
  if (costToGenerate == null || idealValueGenerationRatio == null) return null;
  if (!Number.isFinite(costToGenerate) || !Number.isFinite(idealValueGenerationRatio)) return null;
  return costToGenerate * idealValueGenerationRatio;
}

export function healthWordFromEntropy(entropy: number | null): string {
  if (entropy == null) {
    return 'Not yet assessed';
  }
  if (entropy <= 3) return 'Healthy';
  if (entropy <= 5) return 'Fair';
  if (entropy <= 7) return 'Strained';
  return 'Critical';
}

export function healthMeaningFromEntropy(entropy: number | null): 'good' | 'warn' | 'alert' | 'muted' {
  if (entropy == null) return 'muted';
  if (entropy <= 3) return 'good';
  if (entropy <= 5) return 'warn';
  return 'alert';
}

/** User-facing efficiency readout — never say "correction factor". */
export function efficiencyPhrase(cf: number | null | undefined): string {
  if (cf == null || Number.isNaN(cf)) {
    return 'Not yet assessed';
  }
  const pct = Math.round(Math.max(0, Math.min(100, cf * 100)));
  return `Running at ${pct}% of potential`;
}

export function entropyBarClass(entropy: number | null): 'good' | 'mid' | 'weak' | 'muted' {
  if (entropy == null) return 'muted';
  if (entropy <= 4) return 'good';
  if (entropy <= 6) return 'mid';
  return 'weak';
}

export function plainHealthLabel(entropy: number | null): string {
  return healthWordFromEntropy(entropy);
}
